import React, {ReactNode} from 'react';
import styled, { css, ThemeProps } from 'styled-components';
import { media } from '../../theme/base/mixins';
import { Section } from '../../components/Section';
import { CType } from '../../provider/Typo3Provider/types';
import { ContentHeader } from '../../components/layout/ContentHeader';
import { withErrorBoundary } from '../../error-handling/withErrorBoundary';
import { Theme } from '../../theme';

// images
import Img1 from '../../theme/images/anniversary10years/busse.jpg';
import Img2 from '../../theme/images/anniversary10years/fahnen.jpg';
import Img3 from '../../theme/images/anniversary10years/event.jpg';
import Img4 from '../../theme/images/anniversary10years/standort3.jpg';

// icons
import Kunden from '../../theme/images/anniversary10years/kunden.svg';
import Laufleistung from '../../theme/images/anniversary10years/laufleistung.svg';
import Typo3Label from "../../provider/Typo3Provider/Typo3Label";

const CountItems: number = 12;

export type Grid10YearsProps = CType;

type Grid10YearsTeaserProps = Grid10YearsTeaserContainerProps & {
    label?: ReactNode;
    value?: ReactNode;
    icon?: string;
};

type Grid10YearsImageTeaserContainerProps = {
    image: string;
}

type Grid10YearsTeaserContainerProps = {
    type: 'light' | 'dark';
}

type Grid10YearsTeaserContainerPropsThemed = Grid10YearsTeaserContainerProps & ThemeProps<Theme>;

const Grid10YearsTeaserContainer = styled.div<Grid10YearsTeaserContainerProps>`
    width: 100%;
    position: relative;
    padding-top: 100%;

    & > div {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        display: grid;
        justify-content: center;
        align-content: center;
        text-align: center;
        padding: ${(props: ThemeProps<Theme>) => props.theme.grid.columnGap/2}px;
    }

    img {
        max-height: 120px;
        margin: 0 auto 3rem;
        width: 100%;

        ${media.medium`
            max-width: 75%;
        `}

        ${media.large`
            max-height: 150px;
        `}
    }

    ${(props: Grid10YearsTeaserContainerProps) =>
        props.type === 'light' &&
        css`
            color: ${(props: Grid10YearsTeaserContainerPropsThemed) => props.theme.colors.secondary};
            background: ${(props: Grid10YearsTeaserContainerPropsThemed) => props.theme.colors.grayLight};
        `}
    }

    ${(props: Grid10YearsTeaserContainerProps) =>
        props.type === 'dark' &&
        css`
            background: ${(props: Grid10YearsTeaserContainerPropsThemed) => props.theme.colors.secondary};
            color: ${(props: Grid10YearsTeaserContainerPropsThemed) => props.theme.colors.white};
        `}
    }
`;

const Grid10YearsTeaserLabel = styled.span`
    margin-bottom: .5em;
    font-weight: 300;
    font-size: 1.6rem;

    ${media.large`
        font-size: 2rem;
    `}
`;

const Grid10YearsTeaserValue = styled.span`
    font-weight: 500;
    font-size: 1.8rem;

    ${media.large`
        font-size: 2.4rem;
    `}
`;

const Grid10YearsImageTeaserContainer = styled.div<Grid10YearsImageTeaserContainerProps>`
    width: 100%;
    position: relative;
    background: url(${(props: Grid10YearsImageTeaserContainerProps) => props.image}) center center no-repeat;
    background-size: cover;
    padding-top: 100%;
`;

const Grid10YearsContainer = styled.div`
    display: grid;
    grid-gap: ${(props: ThemeProps<Theme>) => props.theme.grid.columnGap}px;
    place-items: start;

    ${Grid10YearsTeaserContainer},
    ${Grid10YearsImageTeaserContainer} {
        ${() => {
            let templateLiteral = ``;
            Array.from({length: CountItems}, (_, i) => i + 1).forEach(n => {
                templateLiteral += `
            &:nth-child(${n}) {
                order: ${n};
            }
        `;
            });
            return templateLiteral;
        }}
    }

    ${Grid10YearsTeaserContainer} {
        &:nth-child(4),
        &:nth-child(7) {
            grid-column: span 2;
            grid-row: span 2;
        }
    }

    ${Grid10YearsImageTeaserContainer} {
        &:nth-child(9) {
            order: 2;
        }

        &:nth-child(10) {
            order: 4;
        }

        &:nth-child(11) {
            order: 6;
        }
    }

    ${media.medium`
      grid-template-columns: repeat(4, 1fr);

        ${Grid10YearsTeaserContainer} {
            &:nth-child(2),
            &:nth-child(5) {
                grid-column: span 2;
                padding-top: calc(50% - ${(props: ThemeProps<Theme>) => props.theme.grid.columnGap/2}px);
            }

            &:nth-child(3) {
                order: 4;
            }

            &:nth-child(4) {
                order: 3;
            }
        }

        ${Grid10YearsImageTeaserContainer}{
            &:nth-child(10) {
                order: 4;
            }

            &:nth-child(11) {
                order: 5;
            }
        }
    `}

    ${media.large`
      grid-template-columns: repeat(6, 1fr);

        ${Grid10YearsTeaserContainer} {
            &:nth-child(6),
            &:nth-child(8) {
                grid-column: span 2;
                padding-top: calc(50% - ${(props: ThemeProps<Theme>) => props.theme.grid.columnGap/2}px);
            }

            &:nth-child(3) {
                grid-row: span 2;
                padding-top: calc(200% + ${(props: ThemeProps<Theme>) => props.theme.grid.columnGap}px);
            }

            &:nth-child(3) {
                order: 3;
            }

            &:nth-child(4) {
                order: 4;
            }
        }

        ${Grid10YearsImageTeaserContainer}{
            &:nth-child(9) {
                order: 2;
                grid-column: span 2;
                padding-top: calc(50% - ${(props: ThemeProps<Theme>) => props.theme.grid.columnGap/2}px);
            }

            &:nth-child(10) {
                order: 4;
            }

            &:nth-child(11) {
                order: 7;
            }
        }
    `}
`;

const Grid10Years = ({...props}: Grid10YearsProps) => {
    return (
        <Section>
            <ContentHeader {...props} />
            <Grid10YearsContainer>
                <Grid10YearsTeaser type="dark" label={<Typo3Label label="anniversary.grid.location.label" />} value={<Typo3Label label="anniversary.grid.location.value" />}/>
                <Grid10YearsTeaser type="light" label={<Typo3Label label="anniversary.grid.sales.label" />} value={<Typo3Label label="anniversary.grid.sales.value" />}/>
                <Grid10YearsTeaser type="dark" label={<Typo3Label label="anniversary.grid.brands.label" />} value={<Typo3Label label="anniversary.grid.brands.value" />}/>
                <Grid10YearsTeaser type="dark" label={<Typo3Label label="anniversary.grid.mileageVehicles.label" />} value={<Typo3Label label="anniversary.grid.mileageVehicles.value" />} icon={Laufleistung}/>
                <Grid10YearsTeaser type="light" label={<Typo3Label label="anniversary.grid.mileageBusses.label" />} value={<Typo3Label label="anniversary.grid.mileageBusses.value" />}/>
                <Grid10YearsTeaser type="dark" label={<Typo3Label label="anniversary.grid.pageImpressions.label" />} value={<Typo3Label label="anniversary.grid.pageImpressions.value" />}/>
                <Grid10YearsTeaser type="light" label={<Typo3Label label="anniversary.grid.customers.label" />} value={<Typo3Label label="anniversary.grid.customers.value" />} icon={Kunden}/>
                <Grid10YearsTeaser type="light" label={<Typo3Label label="anniversary.grid.constructionYear.label" />} value={<Typo3Label label="anniversary.grid.constructionYear.value" />}/>
                <Grid10YearsImageTeaserContainer image={Img1}/>
                <Grid10YearsImageTeaserContainer image={Img2}/>
                <Grid10YearsImageTeaserContainer image={Img3}/>
                <Grid10YearsImageTeaserContainer image={Img4}/>
            </Grid10YearsContainer>
        </Section>
    );
}

const Grid10YearsTeaser = ({type, label, value, icon}: Grid10YearsTeaserProps) => {
    return (
        <Grid10YearsTeaserContainer type={type}>
            <div>
                {icon && <img src={icon} alt="" />}
                {label && <Grid10YearsTeaserLabel>{label}</Grid10YearsTeaserLabel>}
                {value && <Grid10YearsTeaserValue>{value}</Grid10YearsTeaserValue>}
            </div>
        </Grid10YearsTeaserContainer>
    );
}

export default withErrorBoundary(
    Grid10Years,
    'Grid10Years'
);
